<script setup lang="ts">
import Draggable from 'vuedraggable'
import type { TableHeader } from '@/models/general/table'

interface IProps {
  modelValue?: boolean
  headers?: TableHeader[]
  defaultHeaders?: TableHeader[]
  embedded?: boolean
}

const props = withDefaults(defineProps<IProps>(), {
  modelValue: false,
  headers: () => ([]),
  defaultHeaders: () => ([]),
  embedded: false,
})

interface CustomEvents {
  (e: 'update:modelValue', newValue: boolean): void
  (e: 'onUpdate', newValue: TableHeader[]): void
  (e: 'onClose'): void
}

const emit = defineEmits<CustomEvents>()

const headersList = ref<TableHeader[]>([])

const normalizeHeaderForCompare = (header: TableHeader) => ({
  key: String(header.key || ''),
  title: String(header.title || ''),
  align: String(header.align || ''),
  sortable: header.sortable !== false,
  selectable: header.selectable !== false,
})

const areHeadersEqual = (left: TableHeader[], right: TableHeader[]) => {
  if (left.length !== right.length)
    return false

  for (let index = 0; index < left.length; index += 1) {
    const leftHeader = normalizeHeaderForCompare(left[index])
    const rightHeader = normalizeHeaderForCompare(right[index])
    if (JSON.stringify(leftHeader) !== JSON.stringify(rightHeader))
      return false
  }

  return true
}

const syncHeadersList = (shouldEmit = false) => {
  const defaultHeadersByKey = new Map(props.defaultHeaders.map(header => [header.key, header]))
  const mergedHeaders: TableHeader[] = []
  const seen = new Set<string>()

  props.headers.forEach((header) => {
    const defaultHeader = defaultHeadersByKey.get(header.key)
    if (!defaultHeader || seen.has(header.key))
      return

    seen.add(header.key)
    mergedHeaders.push({
      ...defaultHeader,
      ...header,
      title: defaultHeader.title || header.title,
      align: header.align ?? defaultHeader.align ?? '',
    })
  })

  props.defaultHeaders.forEach((header) => {
    if (seen.has(header.key))
      return

    seen.add(header.key)
    mergedHeaders.push({ ...header })
  })

  headersList.value = JSON.parse(JSON.stringify(mergedHeaders))

  if (shouldEmit && !areHeadersEqual(mergedHeaders, props.headers))
    emit('onUpdate', JSON.parse(JSON.stringify(headersList.value)))
}

const closeDialog = () => {
  emit('update:modelValue', !props.modelValue)
}

const update = async () => {
  emit('onUpdate', headersList.value)
  closeDialog()
}

const onClose = async () => {
  emit('onClose')
}

const emitEmbeddedUpdate = () => {
  if (props.embedded)
    emit('onUpdate', JSON.parse(JSON.stringify(headersList.value)))
}

const toggleAll = (selectAll: boolean) => {
  headersList.value = headersList.value.map(header => ({
    ...header,
    align: header.selectable === false ? header.align : (selectAll ? '' : ' d-none')
  }))

  emitEmbeddedUpdate()
}

const allSelected = computed(() => {
  const selectableHeaders = headersList.value.filter(h => h.selectable !== false)
  return selectableHeaders.length > 0 &&
    selectableHeaders.every(header => !header.align?.includes('d-none'))
})

const someSelected = computed(() => {
  const selectableHeaders = headersList.value.filter(h => h.selectable !== false)
  return selectableHeaders.some(header => !header.align?.includes('d-none')) &&
    !allSelected.value
})

const toggleHeader = (header: TableHeader, value: boolean) => {
  if (header.selectable === false) return
  header.align = value ? '' : ' d-none'
  emitEmbeddedUpdate()
}

const onReorder = () => {
  emitEmbeddedUpdate()
}

onMounted(() => {
  syncHeadersList(true)
})

watch(() => props.modelValue, () => {
  if (props.modelValue)
    syncHeadersList(true)
})

watch(
  () => [props.headers, props.defaultHeaders, props.embedded],
  () => {
    if (props.embedded)
      syncHeadersList(true)
  },
  { deep: true },
)

const filteredColumnsListForCountOnly = computed(() => {
  return headersList.value?.filter(header => !['action', 'actions', 'view'].includes(header?.key) && header?.selectable !== false)
})

const selectedColumnsCount = computed(() => {
  return filteredColumnsListForCountOnly.value?.filter(header => !header?.align?.includes('d-none'))?.length || 0
})

const allColumnsCount = computed(() => {
  return filteredColumnsListForCountOnly.value?.length || 0
})
</script>

<template>
  <div v-if="embedded" class="table-column-settings-embedded">
    <Draggable
      v-model="headersList"
      item-key="key"
      class="draggable-list"
      ghost-class="ghost-draggable"
      handle=".drag-handle"
      @change="onReorder"
    >
      <template #item="{ element }">
        <div class="d-flex align-center pa-2 draggable-item">
          <VIcon
            icon="mdi-drag"
            class="drag-handle mr-2"
          />
          <span class="ml-2" :class="{ 'text-disabled': element.selectable === false }">
            {{ element.title }}
          </span>
        </div>
      </template>
    </Draggable>
  </div>

  <VDialog
    v-else
    :model-value="modelValue"
    max-width="600"
    class="default-dialog"
    @after-leave="onClose"
    @update:modelValue="emit('update:modelValue', !!$event)"
  >
    <VBtn
      variant="elevated"
      density="compact"
      icon="tabler-x"
      class="v-dialog-close-btn"
      @click="closeDialog"
    />

    <VCard title="Table Column Settings">
      <VCardText>
        <VRow>
          <VCol cols="12">
            <div class="d-flex align-center mb-2">
              <VCheckbox
                :model-value="allSelected"
                :indeterminate="someSelected"
                :disabled="headersList.filter(h => h.selectable !== false).length === 0"
                hide-details
                @update:model-value="toggleAll($event)"
              />
              <span
                class="cursor-pointer"
                @click="toggleAll(!allSelected)"
              >
                Toggle all columns
              </span>
            </div>

            <Draggable
              v-model="headersList"
              item-key="key"
              class="draggable-list"
              ghost-class="ghost-draggable"
              handle=".drag-handle"
              @change="onReorder"
            >
              <template #item="{ element }">
                <div class="d-flex align-center pa-2 draggable-item">
                  <VIcon
                    icon="mdi-drag"
                    class="drag-handle mr-2"
                  />
                  <VCheckbox
                    :model-value="!element.align?.includes('d-none')"
                    :disabled="element.selectable === false"
                    hide-details
                    class="ml-2"
                    @update:model-value="toggleHeader(element, $event)"
                  />
                  <span class="ml-2" :class="{ 'text-disabled': element.selectable === false }">
                    {{ element.title }}
                  </span>
                </div>
              </template>
            </Draggable>
          </VCol>
          <VCol
            class="d-flex justify-end"
            cols="12"
          >
            <div class="d-flex align-center justify-center mr-5">
              {{ selectedColumnsCount }}
              /
              {{ allColumnsCount }}
            </div>
            <VBtn @click="update">
              Update
            </VBtn>
          </VCol>
        </VRow>
      </VCardText>
    </VCard>
  </VDialog>
</template>

<style scoped lang="scss">
.draggable-list {
  border: 1px solid rgba(0, 0, 0, 0.12);
  border-radius: 4px;
}

.table-column-settings-embedded {
  width: 100%;
}

.draggable-item {
  border-bottom: 1px solid rgba(0, 0, 0, 0.12);
  background: rgb(var(--v-theme-surface));
}

.draggable-item:last-child {
  border-bottom: none;
}

.drag-handle {
  cursor: move;
  opacity: 0.6;
}

.v-dialog-close-btn {
  position: absolute;
  right: 10px;
  top: 10px;
  z-index: 1;
}

.ghost-draggable {
  opacity: 0.5;
  background: rgb(var(--v-theme-primary), 0.2);
}

.invisible {
  visibility: hidden;
}
</style>
